import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./CookieConsent.scss";

/*
 * Баннер согласия на использование файлов cookie.
 * Соответствие ФЗ-152: Яндекс.Метрика загружается ТОЛЬКО после явного согласия.
 * НАСТРОЙКА: укажите номер счётчика Яндекс.Метрики вместо XXXXXXXX.
 */
const YM_COUNTER_ID = "XXXXXXXX"; // <-- замените на номер вашего счётчика (например, 98765432)
const STORAGE_KEY = "ommatid_cookie_consent"; // 'accepted' | 'declined'

declare global {
  interface Window {
    ym?: (...args: any[]) => void;
    __ommatidMetrikaLoaded?: boolean;
  }
}

function loadMetrika() {
  if (!YM_COUNTER_ID || YM_COUNTER_ID === "XXXXXXXX") return; // счётчик ещё не настроен
  if (window.__ommatidMetrikaLoaded) return;
  window.__ommatidMetrikaLoaded = true;

  (function (m: any, e: Document, t: string, r: string, i: string) {
    m[i] =
      m[i] ||
      function () {
        (m[i].a = m[i].a || []).push(arguments);
      };
    m[i].l = 1 * (new Date() as any);
    for (let j = 0; j < e.scripts.length; j++) {
      if (e.scripts[j].src === r) return;
    }
    const k = e.createElement(t) as HTMLScriptElement;
    const a = e.getElementsByTagName(t)[0];
    k.async = true;
    k.src = r;
    a.parentNode!.insertBefore(k, a);
  })(window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");

  window.ym!(Number(YM_COUNTER_ID), "init", {
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
    webvisor: true,
  });
}

function getConsent(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}
function setConsent(v: string) {
  try {
    window.localStorage.setItem(STORAGE_KEY, v);
  } catch {}
}

const CookieIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5zm-3.5 7A1.5 1.5 0 1 1 7 10.5 1.5 1.5 0 0 1 8.5 9zM8 15.5A1.5 1.5 0 1 1 6.5 17 1.5 1.5 0 0 1 8 15.5zm6 .5a1.5 1.5 0 1 1-1.5 1.5A1.5 1.5 0 0 1 14 16zm2.5-4A1.5 1.5 0 1 1 15 14a1.5 1.5 0 0 1 1.5-2z" />
  </svg>
);

const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(false); // баннер показан
  const [decided, setDecided] = useState(true); // выбор уже сделан

  useEffect(() => {
    const consent = getConsent();
    if (consent === "accepted") loadMetrika();
    if (consent !== "accepted" && consent !== "declined") {
      setDecided(false);
      const t = setTimeout(() => setVisible(true), 700);
      return () => clearTimeout(t);
    }
  }, []);

  const accept = () => {
    setConsent("accepted");
    loadMetrika();
    setVisible(false);
    setDecided(true);
  };
  const decline = () => {
    setConsent("declined");
    setVisible(false);
    setDecided(true);
  };
  const reopen = () => setVisible(true);

  return (
    <>
      <div
        className={"cookie-consent" + (visible ? " cookie-consent--show" : "")}
        role="dialog"
        aria-live="polite"
        aria-label="Уведомление об использовании файлов cookie"
      >
        <div className="cookie-consent__body">
          <p className="cookie-consent__title">Мы используем файлы cookie</p>
          <p className="cookie-consent__text">
            Мы применяем файлы cookie и сервис веб-аналитики Яндекс.Метрика, чтобы
            отслеживать активность на сайте и делать его удобнее. Нажимая
            «Принять», вы даёте согласие на обработку данных в соответствии с{" "}
            <Link to="/policy" className="cookie-consent__link">
              Политикой обработки персональных данных
            </Link>
            . Вы можете отказаться — тогда аналитические cookie использоваться не
            будут.
          </p>
        </div>
        <div className="cookie-consent__actions">
          <button
            type="button"
            className="cookie-consent__btn cookie-consent__btn--decline"
            onClick={decline}
          >
            Отклонить
          </button>
          <button
            type="button"
            className="cookie-consent__btn cookie-consent__btn--accept"
            onClick={accept}
          >
            Принять
          </button>
        </div>
      </div>

      {decided && !visible && (
        <button
          type="button"
          className="cookie-consent-fab"
          aria-label="Настройки файлов cookie"
          title="Настройки cookie"
          onClick={reopen}
        >
          <CookieIcon />
        </button>
      )}
    </>
  );
};

export default CookieConsent;
